import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useParams, useSearchParams } from 'react-router';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import * as Dialog from '@radix-ui/react-dialog';
import { ChevronDown, SlidersHorizontal, X } from 'lucide-react';
import { CONDITIONS, STORE_SORTS, type Condition, type Paginated, type ProductCard, type StoreFacets, type Taxonomy } from '@shop/shared';
import { api, ApiError, qs } from '../lib/api';
import { Link, useFormat, useLocale, useT } from '../lib/i18n';
import { useMoney } from '../lib/settings';
import { useSeo } from '../lib/seo';
import { ProductGrid } from '../components/ProductCard';
import { Breadcrumbs, Button, ErrorState, Img, Ornament } from '../components/ui';
import { NotFoundPage } from './NotFound';

type Mode = 'all' | 'category' | 'collection' | 'search';

const PRICE_BANDS = [
  { min: undefined, max: 1000 },
  { min: 1000, max: 2500 },
  { min: 2500, max: 5000 },
  { min: 5000, max: undefined },
];

const csv = (v: string | null) => (v ? v.split(',').filter(Boolean) : []);

export function ShopPage({ mode }: { mode: Mode }) {
  const { slug } = useParams();
  const [params, setParams] = useSearchParams();
  const money = useMoney();
  const t = useT();
  const f = useFormat();
  const locale = useLocale();
  const [filtersOpen, setFiltersOpen] = useState(false);

  const filters = {
    q: params.get('q') ?? undefined,
    decade: params.get('decade') ?? undefined,
    condition: params.get('condition') ?? undefined,
    minPrice: params.get('minPrice') ?? undefined,
    maxPrice: params.get('maxPrice') ?? undefined,
    inStock: params.get('inStock') === 'true' ? 'true' : undefined,
    onSale: params.get('onSale') === 'true' ? 'true' : undefined,
    sort: (STORE_SORTS as readonly string[]).includes(params.get('sort') ?? '') ? params.get('sort')! : 'featured',
    category: mode === 'category' ? slug : (params.get('category') ?? undefined),
    collection: mode === 'collection' ? slug : undefined,
  };

  const taxonomy = useQuery({
    queryKey: [mode, slug, locale],
    queryFn: () => api<Taxonomy>(`/api/store/${mode === 'category' ? 'categories' : 'collections'}/${slug}`),
    enabled: (mode === 'category' || mode === 'collection') && Boolean(slug),
    retry: (count, err) => !(err instanceof ApiError && err.status === 404) && count < 2,
  });

  const facets = useQuery({
    queryKey: ['facets', locale, filters.category, filters.collection],
    queryFn: () => api<StoreFacets>(`/api/store/facets${qs({ category: filters.category, collection: filters.collection })}`),
    staleTime: 60_000,
  });

  const products = useInfiniteQuery({
    queryKey: ['listing', locale, filters],
    queryFn: ({ pageParam }) => api<Paginated<ProductCard>>(`/api/store/products${qs({ ...filters, page: pageParam, pageSize: 12 })}`),
    initialPageParam: 1,
    getNextPageParam: (last) => (last.page < last.pageCount ? last.page + 1 : undefined),
  });

  const items = useMemo(() => products.data?.pages.flatMap((p) => p.items) ?? [], [products.data]);
  const total = products.data?.pages[0]?.total ?? 0;

  const title =
    mode === 'search'
      ? filters.q
        ? t.shop.resultsFor(filters.q)
        : t.shop.search
      : mode === 'all'
        ? filters.onSale
          ? t.common.reduced
          : t.common.shopAll
        : (taxonomy.data?.name ?? '');
  const description = mode === 'all' || mode === 'search' ? '' : (taxonomy.data?.description ?? '');
  // The full listing is the home page, which keeps the shop's own title.
  const isHome = mode === 'all' && !filters.onSale;
  useSeo({ title: isHome ? undefined : title || undefined, description: description || undefined, image: taxonomy.data?.imageUrl, noindex: mode === 'search' });

  const update = (patch: Record<string, string | undefined>) => {
    const next = new URLSearchParams(params);
    for (const [k, v] of Object.entries(patch)) {
      if (v === undefined || v === '') next.delete(k);
      else next.set(k, v);
    }
    setParams(next, { replace: true, preventScrollReset: true });
  };
  const toggleIn = (key: 'decade' | 'condition', value: string) => {
    const current = csv(params.get(key));
    const next = current.includes(value) ? current.filter((x) => x !== value) : [...current, value];
    update({ [key]: next.join(',') || undefined });
  };

  const activeDecades = csv(filters.decade ?? null);
  const activeConditions = csv(filters.condition ?? null).filter((c): c is Condition => (CONDITIONS as readonly string[]).includes(c));
  const chips: { label: string; clear: () => void }[] = [
    ...(mode !== 'category' && filters.category
      ? [{ label: facets.data?.categories.find((c) => c.slug === filters.category)?.name ?? filters.category, clear: () => update({ category: undefined }) }]
      : []),
    ...activeDecades.map((d) => ({ label: f.decade(Number(d)), clear: () => toggleIn('decade', d) })),
    ...activeConditions.map((c) => ({ label: f.condition(c), clear: () => toggleIn('condition', c) })),
    ...(filters.minPrice || filters.maxPrice
      ? [
          {
            label: `${filters.minPrice ? money(Number(filters.minPrice)) : money(0)} – ${filters.maxPrice ? money(Number(filters.maxPrice)) : t.shop.any}`,
            clear: () => update({ minPrice: undefined, maxPrice: undefined }),
          },
        ]
      : []),
    ...(filters.inStock ? [{ label: t.shop.available, clear: () => update({ inStock: undefined }) }] : []),
    ...(filters.onSale && mode !== 'all' ? [{ label: t.common.reduced, clear: () => update({ onSale: undefined }) }] : []),
  ];

  if (taxonomy.error instanceof ApiError && taxonomy.error.status === 404) return <NotFoundPage />;

  const filterPanel = (
    <FilterPanel
      mode={mode}
      facets={facets.data}
      filters={filters}
      activeDecades={activeDecades}
      activeConditions={activeConditions}
      onToggle={toggleIn}
      onUpdate={update}
    />
  );

  return (
    <div className="pb-24">
      {/* Header */}
      {(mode === 'category' || mode === 'collection') && taxonomy.data?.imageUrl ? (
        <section className="mb-10 border-b border-dashed border-ink/25 bg-sand/45 md:mb-14">
          <div className="container-page grid items-center gap-8 py-10 md:grid-cols-[minmax(0,1fr)_auto] md:gap-14 md:py-12">
            <div>
              <Breadcrumbs items={[{ label: t.common.home, to: '/' }, { label: taxonomy.data.name }]} />
              <h1 className="font-display mt-6 text-[2.9rem] leading-none font-semibold md:text-[4.6rem]">{taxonomy.data.name}</h1>
              <Ornament className="mt-6 justify-start!" />
              {description && <p className="mt-5 max-w-md text-[18px] leading-relaxed text-ink/75 italic">{description}</p>}
            </div>
            <div className="photo w-full rotate-[1.2deg] md:w-[340px]">
              <div className="photo-inner grain aspect-[4/3] md:aspect-[4/5]">
                <Img src={taxonomy.data.imageUrl} alt="" priority sizes="(min-width: 768px) 340px, 100vw" className="absolute inset-0 h-full w-full object-cover" />
              </div>
            </div>
          </div>
        </section>
      ) : (
        <section className={`container-page ${mode === 'all' ? 'pt-7 pb-6 md:pt-10 md:pb-8' : 'pt-8 pb-10 md:pt-12 md:pb-14'}`}>
          {mode !== 'all' && <Breadcrumbs items={[{ label: t.common.home, to: '/' }, { label: title || '…' }]} />}
          <h1 className={`font-display leading-none font-semibold ${mode === 'all' ? 'text-[2.3rem] md:text-[3.2rem]' : 'mt-6 text-[2.9rem] md:text-[4.6rem]'}`}>{title || ' '}</h1>
          {mode !== 'all' && <Ornament className="mt-6 justify-start!" />}
          {description && <p className="mt-5 max-w-xl text-ink/70">{description}</p>}
        </section>
      )}

      <div className="container-page">
        {/* Toolbar */}
        <div className="sticky top-16 z-20 -mx-4 mb-8 flex items-center justify-between gap-4 border-y border-ink/20 bg-bone/95 px-4 py-3 backdrop-blur sm:-mx-6 sm:px-6 md:top-[91px] lg:mx-0 lg:px-0">
          <div className="flex items-center gap-3 sm:gap-4">
            <button onClick={() => setFiltersOpen(true)} className="typewriter inline-flex items-center gap-2 text-[12.5px] font-bold tracking-[0.12em] uppercase lg:hidden">
              <SlidersHorizontal className="h-4 w-4" strokeWidth={1.6} /> {t.shop.filter}
              {chips.length > 0 && <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-ink px-1 text-[10px] text-paper">{chips.length}</span>}
            </button>
            <p className="typewriter text-[13px] whitespace-nowrap text-stone" aria-live="polite">
              {products.isLoading ? t.common.loading : t.common.items(total)}
            </p>
          </div>
          <label className="relative inline-flex items-center gap-2 text-[14px]">
            <span className="typewriter hidden text-[12.5px] tracking-[0.06em] text-stone uppercase sm:inline">{t.shop.sortBy}</span>
            <select
              value={filters.sort}
              onChange={(e) => update({ sort: e.target.value === 'featured' ? undefined : e.target.value })}
              className="max-w-[9.5rem] appearance-none truncate bg-transparent pr-6 font-semibold outline-none sm:max-w-none"
              aria-label={t.shop.sortLabel}
            >
              {STORE_SORTS.map((s) => (
                <option key={s} value={s}>
                  {t.shop.sorts[s]}
                </option>
              ))}
            </select>
            <ChevronDown className="pointer-events-none absolute right-0 h-3.5 w-3.5" />
          </label>
        </div>

        <div className="grid gap-12 lg:grid-cols-[240px_1fr] xl:gap-16">
          <aside className="hidden lg:block">
            <div className="sticky top-[165px] max-h-[calc(100vh-185px)] overflow-y-auto pr-2 pb-8">{filterPanel}</div>
          </aside>

          <div>
            {chips.length > 0 && (
              <div className="mb-8 flex flex-wrap items-center gap-2">
                {chips.map((c) => (
                  <button key={c.label} onClick={c.clear} className="typewriter inline-flex items-center gap-1.5 rounded-[2px] border border-ink/30 bg-paper py-1.5 pr-2.5 pl-3 text-[13px] transition hover:border-ink">
                    {c.label} <X className="h-3 w-3" />
                  </button>
                ))}
                <button onClick={() => setParams(filters.q ? { q: filters.q } : {}, { replace: true })} className="link-underline-static ml-2 text-[14px] text-stone hover:text-ink">
                  {t.shop.clearAll}
                </button>
              </div>
            )}

            {products.error ? (
              <ErrorState message={(products.error as Error).message} onRetry={() => products.refetch()} />
            ) : !products.isLoading && items.length === 0 ? (
              <div className="flex flex-col items-center py-24 text-center">
                <p className="font-display text-3xl font-semibold">{t.shop.nothing}</p>
                {(mode !== 'all' || chips.length > 0) && (
                  <>
                    <p className="mt-3 max-w-sm text-stone">{mode === 'search' ? t.shop.tryDifferent : t.shop.tryRemoving}</p>
                    <Link to="/" className="btn btn-secondary mt-8 h-12 px-6">
                      {t.shop.browseEverything}
                    </Link>
                  </>
                )}
              </div>
            ) : (
              <>
                <ProductGrid products={items} loading={products.isLoading} count={6} columns={3} priorityCount={3} />
                {products.hasNextPage && (
                  <div className="mt-16 flex flex-col items-center gap-4">
                    <p className="typewriter text-[13px] text-stone">{t.shop.showing(items.length, total)}</p>
                    <div className="h-[2px] w-48 overflow-hidden rounded bg-line">
                      <div className="h-full bg-ink transition-all" style={{ width: `${(items.length / Math.max(total, 1)) * 100}%` }} />
                    </div>
                    <Button variant="secondary" onClick={() => products.fetchNextPage()} loading={products.isFetchingNextPage}>
                      {t.shop.loadMore}
                    </Button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      <Dialog.Root open={filtersOpen} onOpenChange={setFiltersOpen}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 z-50 bg-ink/40 data-[state=closed]:animate-fade-out data-[state=open]:animate-fade-in" />
          <Dialog.Content className="fixed inset-y-0 left-0 z-50 flex w-[88vw] max-w-sm flex-col bg-bone data-[state=closed]:animate-slide-out-left data-[state=open]:animate-slide-in-left">
            <div className="flex h-16 items-center justify-between border-b-[3px] border-double border-ink/60 px-5">
              <Dialog.Title className="font-display text-2xl font-semibold">{t.shop.filter}</Dialog.Title>
              <Dialog.Close className="-mr-2 p-2" aria-label={t.shop.closeFilters}>
                <X className="h-5 w-5" strokeWidth={1.6} />
              </Dialog.Close>
            </div>
            <Dialog.Description className="sr-only">{t.shop.narrow}</Dialog.Description>
            <div className="flex-1 overflow-y-auto px-5 py-6">{filterPanel}</div>
            <div className="border-t border-ink/20 p-5">
              <Button className="w-full" onClick={() => setFiltersOpen(false)}>
                {t.shop.show(total)}
              </Button>
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    </div>
  );
}

function FilterGroup({ title, children }: { title: string; children: ReactNode }) {
  return (
    <details open className="group border-b border-dashed border-ink/25 py-5 first:pt-0">
      <summary className="typewriter flex cursor-pointer list-none items-center justify-between text-[12px] font-bold tracking-[0.18em] uppercase [&::-webkit-details-marker]:hidden">
        {title}
        <ChevronDown className="h-3.5 w-3.5 transition-transform group-open:rotate-180" />
      </summary>
      <div className="pt-4">{children}</div>
    </details>
  );
}

function Toggle({ checked, onChange, label, count }: { checked: boolean; onChange: (v: boolean) => void; label: string; count?: number }) {
  return (
    <label className="flex cursor-pointer items-center justify-between py-1 text-[16px]">
      <span className="flex items-center gap-3">
        <span className={`flex h-4 w-4 items-center justify-center border-[1.5px] transition ${checked ? 'border-ink bg-ink' : 'border-ink/40 bg-paper'}`}>
          {checked && (
            <svg viewBox="0 0 12 12" className="h-2.5 w-2.5 text-paper">
              <path d="M2 6.5 4.8 9 10 3" fill="none" stroke="currentColor" strokeWidth="1.8" />
            </svg>
          )}
        </span>
        {label}
      </span>
      {count !== undefined && <span className="typewriter text-[12px] text-ash">{count}</span>}
      <input type="checkbox" className="sr-only" checked={checked} onChange={(e) => onChange(e.target.checked)} />
    </label>
  );
}

function FilterPanel({
  mode,
  facets,
  filters,
  activeDecades,
  activeConditions,
  onToggle,
  onUpdate,
}: {
  mode: Mode;
  facets: StoreFacets | undefined;
  filters: { category?: string; minPrice?: string; maxPrice?: string; inStock?: string; onSale?: string };
  activeDecades: string[];
  activeConditions: Condition[];
  onToggle: (key: 'decade' | 'condition', value: string) => void;
  onUpdate: (patch: Record<string, string | undefined>) => void;
}) {
  const t = useT();
  const f = useFormat();
  const money = useMoney();
  const [min, setMin] = useState(filters.minPrice ? String(Number(filters.minPrice) / 100) : '');
  const [max, setMax] = useState(filters.maxPrice ? String(Number(filters.maxPrice) / 100) : '');
  useEffect(() => {
    setMin(filters.minPrice ? String(Number(filters.minPrice) / 100) : '');
    setMax(filters.maxPrice ? String(Number(filters.maxPrice) / 100) : '');
  }, [filters.minPrice, filters.maxPrice]);
  const applyPrice = () => {
    const toMinor = (v: string) => (v && Number(v.replace(',', '.')) >= 0 ? String(Math.round(Number(v.replace(',', '.')) * 100)) : undefined);
    onUpdate({ minPrice: toMinor(min), maxPrice: toMinor(max) });
  };
  const bandLabel = (b: (typeof PRICE_BANDS)[number]) =>
    b.min === undefined ? t.shop.under(money(b.max!)) : b.max === undefined ? t.shop.over(money(b.min)) : `${money(b.min)} – ${money(b.max)}`;
  const chip = (active: boolean) => `typewriter rounded-[2px] border px-3 py-1.5 text-[13px] transition ${active ? 'border-ink bg-ink text-paper' : 'border-ink/30 bg-paper hover:border-ink'}`;

  return (
    <div>
      {mode !== 'category' && facets && facets.categories.length > 0 && (
        <FilterGroup title={t.shop.category}>
          <ul className="space-y-2.5 text-[16.5px]">
            {facets.categories.map((c) => (
              <li key={c.slug}>
                <button
                  onClick={() => onUpdate({ category: filters.category === c.slug ? undefined : c.slug })}
                  className={`flex w-full items-center justify-between text-left transition ${filters.category === c.slug ? 'font-semibold text-ink' : 'text-ink/75 hover:text-ink'}`}
                >
                  <span className={filters.category === c.slug ? 'link-underline-static' : ''}>{c.name}</span>
                  <span className="typewriter text-[12px] text-ash">{c.count}</span>
                </button>
              </li>
            ))}
          </ul>
        </FilterGroup>
      )}
      {facets && facets.decades.length > 0 && (
        <FilterGroup title={t.shop.decade}>
          <div className="flex flex-wrap gap-2">
            {facets.decades.map(({ decade, count }) => (
              <button key={decade} onClick={() => onToggle('decade', String(decade))} className={chip(activeDecades.includes(String(decade)))} aria-pressed={activeDecades.includes(String(decade))}>
                {f.decade(decade)} <span className="opacity-60">({count})</span>
              </button>
            ))}
          </div>
        </FilterGroup>
      )}
      {facets && facets.conditions.length > 0 && (
        <FilterGroup title={t.shop.condition}>
          {facets.conditions.map(({ condition, count }) => (
            <Toggle key={condition} checked={activeConditions.includes(condition)} onChange={() => onToggle('condition', condition)} label={f.condition(condition)} count={count} />
          ))}
        </FilterGroup>
      )}
      <FilterGroup title={t.shop.availability}>
        <Toggle checked={filters.inStock === 'true'} onChange={(v) => onUpdate({ inStock: v ? 'true' : undefined })} label={t.shop.hideSold} />
        {(facets?.onSaleCount ?? 0) > 0 && (
          <Toggle checked={filters.onSale === 'true'} onChange={(v) => onUpdate({ onSale: v ? 'true' : undefined })} label={t.common.reduced} count={facets?.onSaleCount} />
        )}
      </FilterGroup>
      <FilterGroup title={t.shop.price}>
        <div className="mb-4 flex flex-wrap gap-2">
          {PRICE_BANDS.map((b) => {
            const active = filters.minPrice === (b.min !== undefined ? String(b.min) : undefined) && filters.maxPrice === (b.max !== undefined ? String(b.max) : undefined);
            return (
              <button
                key={`${b.min}-${b.max}`}
                onClick={() =>
                  onUpdate(active ? { minPrice: undefined, maxPrice: undefined } : { minPrice: b.min !== undefined ? String(b.min) : undefined, maxPrice: b.max !== undefined ? String(b.max) : undefined })
                }
                className={chip(active)}
                aria-pressed={active}
              >
                {bandLabel(b)}
              </button>
            );
          })}
        </div>
        <form
          className="flex items-center gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            applyPrice();
          }}
        >
          <input
            inputMode="decimal"
            value={min}
            onChange={(e) => setMin(e.target.value.replace(/[^0-9.,]/g, ''))}
            onBlur={applyPrice}
            placeholder={facets ? `${t.shop.min} ${Math.floor(facets.price.min / 100)}` : t.shop.min}
            className="field h-10 py-0 text-[15px]"
            aria-label={t.shop.minPrice}
          />
          <span className="text-stone">–</span>
          <input
            inputMode="decimal"
            value={max}
            onChange={(e) => setMax(e.target.value.replace(/[^0-9.,]/g, ''))}
            onBlur={applyPrice}
            placeholder={facets ? `${t.shop.max} ${Math.ceil(facets.price.max / 100)}` : t.shop.max}
            className="field h-10 py-0 text-[15px]"
            aria-label={t.shop.maxPrice}
          />
        </form>
      </FilterGroup>
    </div>
  );
}
